import { useCallback, useEffect, useMemo, useState } from 'react';
import { resolvePreviewEntryDisplayUrl } from '../../utils/mediaHelpers';
import type {
  StoryboardSegment,
  StoryboardSegmentPreviewEntry,
  StoryboardSegmentTask,
} from '../../types';
import type { StoryboardContextValue } from '../../context';
import { deriveActiveTasks } from './taskUtils';

export interface TimelineSelection {
  infoSegment: StoryboardSegment | null;
  previewEntries: StoryboardSegmentPreviewEntry[];
  selectedPreviewId: string | null;
  activePreviewId: string | null;
  tasks: StoryboardSegmentTask[];
}

interface UseTimelineSelectionParams
  extends Pick<
    StoryboardContextValue,
    'segments' | 'selectSegment' | 'focusPreviewEntry' | 'updateSegment'
  > {
  focusedPreviewEntryIds: StoryboardContextValue['focusedPreviewEntryIds'];
}

interface UseTimelineSelectionResult {
  selection: TimelineSelection;
  infoAnchorEl: HTMLElement | null;
  infoSegmentId: string | null;
  infoArrowRef: HTMLDivElement | null;
  setInfoAnchorEl: (anchor: HTMLElement | null) => void;
  setInfoSegmentId: (segmentId: string | null) => void;
  setInfoArrowRef: (element: HTMLDivElement | null) => void;
  handleSelectSegmentCard: (segmentId: string, anchor: HTMLElement) => void;
  handleCloseInfo: () => void;
  handlePreviewEntry: (entry: StoryboardSegmentPreviewEntry) => void;
  handleAdoptPreview: (entry: StoryboardSegmentPreviewEntry) => Promise<void>;
}

export const useTimelineSelection = (
  params: UseTimelineSelectionParams
): UseTimelineSelectionResult => {
  const { segments, selectSegment, focusPreviewEntry, updateSegment, focusedPreviewEntryIds } = params;
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLElement | null>(null);
  const [infoSegmentId, setInfoSegmentId] = useState<string | null>(null);
  const [infoArrowRef, setInfoArrowRef] = useState<HTMLDivElement | null>(null);

  const infoSegment = useMemo(
    () => segments.find((segment) => segment.segmentId === infoSegmentId) ?? null,
    [segments, infoSegmentId]
  );

  useEffect(() => {
    if (infoSegmentId && !infoSegment) {
      setInfoAnchorEl(null);
      setInfoSegmentId(null);
      setInfoArrowRef(null);
    }
  }, [infoSegment, infoSegmentId]);

  const previewEntries = useMemo(() => {
    if (!infoSegment) {
      return [];
    }
    const entries = infoSegment.previewEntries ?? [];
    return entries
      .slice()
      .sort(
        (left, right) =>
          (right.createdAt ?? 0) - (left.createdAt ?? 0)
      );
  }, [infoSegment]);
  const selectedPreviewId = infoSegment?.selectedPreviewId ?? null;
  const focusedPreviewId = infoSegment ? focusedPreviewEntryIds[infoSegment.segmentId] ?? null : null;
  const activePreviewId =
    focusedPreviewId ??
    selectedPreviewId ??
    (previewEntries.length ? previewEntries[0]?.previewId ?? null : null);

  const tasks = useMemo(() => deriveActiveTasks(infoSegment), [infoSegment]);

  const handleSelectSegmentCard = useCallback(
    (segmentId: string, anchor: HTMLElement) => {
      selectSegment(segmentId);
      setInfoAnchorEl(() => (infoSegmentId === segmentId ? null : anchor));
      setInfoSegmentId((current) => (current === segmentId ? null : segmentId));
      setInfoArrowRef(null);
    },
    [infoSegmentId, selectSegment]
  );

  const handleCloseInfo = useCallback(() => {
    setInfoAnchorEl(null);
    setInfoSegmentId(null);
    setInfoArrowRef(null);
  }, []);

  const handlePreviewEntry = useCallback(
    (entry: StoryboardSegmentPreviewEntry) => {
      if (!infoSegment) return;
      focusPreviewEntry(infoSegment.segmentId, entry.previewId);
    },
    [focusPreviewEntry, infoSegment]
  );

  const handleAdoptPreview = useCallback(
    async (entry: StoryboardSegmentPreviewEntry) => {
      if (!infoSegment) return;
      if (infoSegment.selectedPreviewId === entry.previewId) return;
      await updateSegment(infoSegment.segmentId, {
        selectedPreviewId: entry.previewId,
        previewUrl: resolvePreviewEntryDisplayUrl(entry),
      });
      focusPreviewEntry(infoSegment.segmentId, entry.previewId);
    },
    [focusPreviewEntry, infoSegment, updateSegment]
  );

  const selection: TimelineSelection = useMemo(
    () => ({
      infoSegment,
      previewEntries,
      selectedPreviewId,
      activePreviewId,
      tasks,
    }),
    [activePreviewId, infoSegment, previewEntries, selectedPreviewId, tasks]
  );

  return {
    selection,
    infoAnchorEl,
    infoSegmentId,
    infoArrowRef,
    setInfoAnchorEl,
    setInfoSegmentId,
    setInfoArrowRef,
    handleSelectSegmentCard,
    handleCloseInfo,
    handlePreviewEntry,
    handleAdoptPreview,
  };
};
